import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Search, Trash2 } from 'lucide-react';
import Modal from '../../components/Modal';
import { BrandSpinnerRing } from '../../components/SiteBrandedLoader';
import { articlesService, ArticleTagUsage } from '../../services/api';
import BlogSectionNav from './BlogSectionNav';
import ConfirmDialog from './ConfirmDialog';
import { apiErrorMessage, faNumber } from './blogAdmin';

/** مدیریت برچسب‌ها: تغییر نام/ادغام و حذف در همهٔ مقالات */
const BlogTags: React.FC = () => {
  const [tags, setTags] = useState<ArticleTagUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [toast, setToast] = useState('');
  const [renaming, setRenaming] = useState<ArticleTagUsage | null>(null);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<ArticleTagUsage | null>(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTags(await articlesService.getTags());
    } catch (err) {
      showToast(apiErrorMessage(err, 'خطا در دریافت برچسب‌ها'));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => (query.trim() ? tags.filter((t) => t.tag.includes(query.trim())) : tags), [tags, query]);

  const rename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renaming || !newName.trim()) return;
    setSaving(true);
    try {
      const res = await articlesService.renameTag(renaming.tag, newName.trim());
      showToast(`برچسب در ${faNumber(res.updated)} مقاله به‌روز شد`);
      setRenaming(null);
      load();
    } catch (err) {
      showToast(apiErrorMessage(err, 'تغییر نام انجام نشد'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const res = await articlesService.deleteTag(toDelete.tag);
      showToast(`برچسب از ${faNumber(res.updated)} مقاله حذف شد`);
      setToDelete(null);
      load();
    } catch (err) {
      showToast(apiErrorMessage(err, 'حذف انجام نشد'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <BlogSectionNav title="برچسب‌های وبلاگ" description="برچسب‌ها در فرم هر مقاله ساخته می‌شوند؛ این‌جا می‌توانید آن‌ها را یکدست، ادغام یا حذف کنید." />

      <label className="relative block max-w-md">
        <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="جست‌وجوی برچسب…" className="w-full rounded-xl border border-gray-200 py-2 pl-3 pr-9 text-sm" />
      </label>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <BrandSpinnerRing size="md" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-600">برچسبی پیدا نشد.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-xs font-bold text-gray-500">
              <tr>
                <th className="px-4 py-3 text-right">برچسب</th>
                <th className="px-4 py-3 text-right">تعداد مقاله</th>
                <th className="px-4 py-3 text-right">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((t) => (
                <tr key={t.tag}>
                  <td className="px-4 py-3 font-bold text-gray-900">#{t.tag}</td>
                  <td className="px-4 py-3 text-xs text-gray-700">
                    <Link to={`/articles?tag=${encodeURIComponent(t.tag)}`} className="hover:text-[#4a2e68]">
                      {faNumber(t.count)} مقاله ({faNumber(t.publishedCount)} منتشرشده)
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          setRenaming(t);
                          setNewName(t.tag);
                        }}
                        className="rounded-lg p-2 text-gray-600 hover:bg-violet-50"
                        title="تغییر نام / ادغام"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </button>
                      <button type="button" onClick={() => setToDelete(t)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="حذف">
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal isOpen={!!renaming} onClose={() => !saving && setRenaming(null)} title="تغییر نام برچسب" size="small">
        <form onSubmit={rename} className="space-y-4">
          <p className="text-sm leading-7 text-gray-600">اگر نام جدید از قبل وجود داشته باشد، دو برچسب در همهٔ مقالات ادغام می‌شوند.</p>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" autoFocus />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setRenaming(null)} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700">
              انصراف
            </button>
            <button type="submit" disabled={saving || !newName.trim()} className="rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
              {saving ? 'در حال ذخیره…' : 'ذخیره'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        danger
        busy={deleting}
        title="حذف برچسب"
        confirmLabel="حذف از همهٔ مقالات"
        message={<>برچسب «{toDelete?.tag}» از {faNumber(toDelete?.count)} مقاله برداشته می‌شود. خود مقالات حذف نمی‌شوند.</>}
        onConfirm={doDelete}
        onClose={() => setToDelete(null)}
      />

      {toast && (
        <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default BlogTags;
